import get from 'lodash/get';
import {useState} from 'react';
import {useNavigate} from "react-router-dom";

import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from '@mui/material/Stack';
import Typography from "@mui/material/Typography";

import * as validators from '../../helpers/validations';
import {getRelations} from '../../helpers/selectOptions';
import CustomTextField from '../common/CustomTextField';
import CustomSelect from '../common/CustomSelect';
import "./GuardianInfo.scss";

const defaultFormValues = {
  firstName:'',
  lastName: '',
  email: '',
  phone: '',
  relation: null,
  hasGuardian: true,
};

const relationOptions = getRelations();

const validations = {
  firstName: validators.name,
  lastName: validators.name,
  email: str => !str || validators.email(str),
  phone: str => !str || validators.phone(str),
  relation: val => !!val,
};

const maskPhone = numStr => {
  if (numStr === '') return '';
  const result = numStr.split('').reduce((acc, next, i) => {
    if (i > -1 && i < 3) return `${acc}${next}`;
    if(i === 3) return `${acc}) ${next}`;
    if (i > 3 && i < 6) return `${acc}${next}`;
    if (i === 6) return `${acc}-${next}`;
    if (i > 9) return acc;
    return `${acc}${next}`;
  }, '(');
  return result;
};

const unmaskPhone = formattedNumStr => (
  formattedNumStr.replace(/[\(\)\s\-]/g, '')
);

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  const value = (
    fieldName === 'phone'
    ? unmaskPhone(get(e, 'target.value', ''))
    : (get(e, 'target.value', '') || '').replace(/^\s/g, '').replace(/\s$/g, '')
  );
  const values = {
    ...formValues,
    [fieldName]: value,
  };
  return setFormValues(values);
};

const checkIfFormIsValid = (formValues, validationDict) => {
  const containsInvalidValues = Object.keys(validationDict).reduce((acc, k) => (
    validationDict[k] ? acc : false
  ), true);
  return containsInvalidValues;
};

const GuardianInfo = props => {
  const navigate = useNavigate();
  const {onSubmit, user} = props;
  const priorValues = (
    user && user.guardian_phone
    ? {
      hasGuardian: true,
      firstName: user.guardian_first_name,
      lastName: user.guardian_last_name,
      relation: user.guardian_relation,
      phone: user.guardian_phone || '',
      email: user.guardian_email,
    }
    : null
  );
  const [formValues, setFormValues] = useState(priorValues || defaultFormValues);
  const [blurred, setBlurred] = useState({});
  const onChangeHandler = handleFieldUpdate(formValues, setFormValues);
  const validationDict = Object.keys(validations).reduce((acc, k) => ({
    ...acc,
    [k]: validations[k](formValues[k])
  }), {});
  const formIsValid = checkIfFormIsValid(
    formValues,
    validationDict
  );
  return (
      <Stack className='GuardianInfo'>
        <Typography variant='large500Tight' marginBottom='26px'>
          Please enter your information as the guardian of the person you are registering for.
        </Typography>

        <Stack direction='row' gap={4.5} justifyContent='space-between'>
          <CustomTextField
            id='firstNameImput'
            placeholder='Jane'
            variant='outlined'
            label='Guardian First Name'
            defaultValue={get(user, 'guardian_first_name')}
            required
            fullWidth
            error={!validationDict.firstName && (blurred.firstName || priorValues)}
            onBlur={() => setBlurred({...blurred, firstName: true})}
            onChange={onChangeHandler('firstName')}
          />
          <CustomTextField
            id='lastNameImput'
            placeholder='Smith'
            variant='outlined'
            label='Guardian Last Name'
            defaultValue={get(user, 'guardian_last_name')}
            required
            fullWidth
            error={!validationDict.lastName && (blurred.lastName || priorValues)}
            onBlur={() => setBlurred({...blurred, lastName: true})}
            onChange={onChangeHandler('lastName')}
          />
        </Stack>
        <CustomTextField
          id='phone'
          label='Guardian Phone'
          placeholder='(123) 456-7890'
          variant='outlined'
          value={maskPhone(
            formValues.phone
            ? formValues.phone
            : get(user, 'guardian_phone', '') || ''
          )}
          error={!validationDict.phone && (blurred.phone || priorValues)}
          onChange={onChangeHandler('phone')}
          onBlur={() => setBlurred({...blurred, phone: true})}
        />
        <CustomTextField
          id='emailInput'
          placeholder='jane.smith@gmail.com'
          defaultValue={get(user, 'guardian_email')}
          variant='outlined'
          label='Guardian Email'
          type='email'
          required
          error={!validationDict.email && (blurred.email || priorValues)}
          onBlur={() => setBlurred({...blurred, email: true})}
          onChange={onChangeHandler('email')}
        />
        <CustomSelect
          id='relation'
          label='Guardian Relation'
          required
          defaultValue={get(user, 'guardian_relation')}
          onChange={onChangeHandler('relation')}
          options={relationOptions}
        />

        <Stack
          direction='column'
          gap='24px'
          paddingTop='9px'
          marginTop={0}
        >
          <Button
            sx={{alignSelf: 'center'}}
            variant='primaryGradient'
            size='medium'
            fullWidth={false}
            disabled={!formIsValid}
            onClick={() => onSubmit(formValues)}
          >
            Create Account
          </Button>

          <Stack direction='row' justifyContent='center'>
            <Typography className="already-have-account" variant='small500xTight'>
              Already have an account?
              {' '}
              <Link onClick={() => navigate('/login')}>Login</Link>
            </Typography>
          </Stack>
        </Stack>
    </Stack>
  );
};

export default GuardianInfo;
